<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_PREPAYMENT_AMORTIZATION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>
        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <b-table
      ref="branchRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="prepaymentAmortizationList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(utilizationStartDate)="data">
        <p class="mb-0">
          {{ toDateWithYearFirst(data.value ) }}
        </p>
      </template>
      <template #cell(amount)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(cummulativeAmortizedValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(netRepaymentValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(monthlyAmortizedValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ formatDate(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_PREPAYMENT_AMORTIZATION')"
              @click="onSelectBranchItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectBranchItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_PREPAYMENT_AMORTIZATION' : 'ACTIVATE_PREPAYMENT_AMORTIZATION')"
              @click="prepaymentStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      modal-footer
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="prepaymentAmortizationRules">
        <b-form>
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="6">
              <InputField
                id="prepayment-name"
                v-model="prepaymentAmortizationFormData.name"
                :label="$t('Prepayment Name')"
                :placeholder="$t('Enter prepayment name')"
                type="text"
                :error-name="$t('Prepayment Name')"
                rules="required"
                for="prepayment-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Name -->
            <!-- Description -->
            <b-col md="6">
              <InputField
                id="description"
                v-model="prepaymentAmortizationFormData.description"
                :label="$t('Prepayment Description')"
                :placeholder="$t('Enter Prepayment Description')"
                type="text"
                :error-name="$t('Prepayment Description')"
                rules="required"
                for="description"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Description -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="prepaymentAmortizationFormData.branch"
                :reduce="option => option.value"
                :label="$t('Branch of use')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                rules="required"
                :error-name="$t('Branch of use')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Branch -->
            <!-- Prepayment Category -->
            <b-col
              md="6"
            >
              <SelectField
                id="prepayment-category"
                v-model="prepaymentAmortizationFormData.category"
                :label="$t('Prepayment Category')"
                :placeholder="$t('Select')"
                :options="categoryOptions"
                :reduce="option => option.value"
                :error-name="$t('Prepayment Category')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Prepayment Catgeory -->
            <b-col md="6">
              <h6 class="mt-0 mb-0">
                {{ $t('Existence') }}
              </h6>
              <b-form-radio-group
                v-model="prepaymentAmortizationFormData.existence"
                :options="translateExistenceOptions"
                class="demo-inline-spacing"
                name="radios-stacked"
                stacked
                value-field="value"
                text-field="text"
                disabled-field="disabled"
                :disabled="isEditable"
                @input="setExistence(prepaymentAmortizationFormData.existence)"
              />
            </b-col>
            <!-- Utilization Start Date -->
            <b-col md="6">
              <DatePickerField
                id="utilization-date"
                v-model="prepaymentAmortizationFormData.utilizationStartDate"
                rules="required"
                :min-date="new Date()"
                label="Utilization Start Date"
                :error-name="$t('Utilization Start Date')"
                :config="datePickerConfig"
                :placeholder="$t('Utilization Start Date')"
                :disabled="isReadonly"
                vid="maxDate"
              />
            </b-col>
            <!-- Utilization Start Date -->

            <!-- Prepayment Amount -->
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="prepayment-amount"
                  v-model="prepaymentAmortizationFormData.amount"
                  :label="$t('Prepayment Amount')"
                  :placeholder="$t('Enter Prepayment Amount')"
                  :error-name="$t('Prepayment Amount')"
                  rules="required|upTo2Decimal"
                  for="prepayment-amount"
                  :readonly="isReadonly"
                  @input="computePrepayment"
                />
              </validationobserver>
            </b-col>
            <!-- Prepayment Amount -->

            <!-- Total Coverage Period (in months) -->
            <b-col md="6">
              <InputField
                id="total-coverage-period"
                v-model="prepaymentAmortizationFormData.totalCoveragePeriod"
                :label="$t('Total Coverage Period (in months)')"
                :placeholder="$t('Enter Coverage Period (in months)')"
                type="number"
                :error-name="$t('Total Coverage Period (in months)')"
                rules="required|integer|positive"
                for="total-coverage-period"
                :readonly="isReadonly"
                @input="computePrepayment"
              />
            </b-col>
            <!-- Total Coverage Period (in months) -->

            <!-- Periods Already Utilized (in months) -->
            <b-col md="6">
              <InputField
                id="period-already-utilized"
                v-model="prepaymentAmortizationFormData.periodsAlreadyUtilized"
                :label="$t('Periods Already Utilized (in months)')"
                :placeholder="$t('Enter Periods Already Utilized (in months)')"
                type="number"
                :error-name="$t('Periods Already Utilized (in months)')"
                rules="required|integer|positive"
                for="period-already-utilized"
                :readonly="isReadonly"
                @input="computePrepayment"
              />
            </b-col>
            <!-- Periods Already Utilized (in months) -->

            <!-- Cumm. Amortized Value -->
            <b-col md="6">
              <SpecialInputNumberField
                id="prepayment-amount"
                v-model="prepaymentAmortizationFormData.cummulativeAmortizedValue"
                :label="$t('Cumm. Amortized Value')"
                :placeholder="$t('Enter Cumm. Amortized Value')"
                type="number"
                :error-name="$t('Cumm. Amortized Value')"
                rules="required"
                for="prepayment-amount"
                readonly
              />
            </b-col>
            <!-- Cumm. Amortized Value -->

            <!-- Un-utilized Coverage Period (in months) -->
            <b-col md="6">
              <InputField
                id="period-already-utilized"
                v-model="prepaymentAmortizationFormData.unutilizedCoveragePeriod"
                :label="$t('Un-utilized Coverage Period (in months)')"
                :placeholder="$t('Enter Un-utilized Coverage Period (in months)')"
                type="number"
                :error-name="$t('Un-utilized Coverage Period (in months)')"
                rules="required"
                for="period-already-utilized"
                readonly
              />
            </b-col>
            <!-- Un-utilized Coverage Period (in months) -->

            <!-- Net Prepayment Value -->
            <b-col md="6">
              <SpecialInputNumberField
                id="period-already-utilized"
                v-model="prepaymentAmortizationFormData.netRepaymentValue"
                :label="$t('Net Prepayment Value')"
                :placeholder="$t('Enter Net Prepayment Value')"
                type="number"
                :error-name="$t('Net Prepayment Value')"
                rules="required"
                for="period-already-utilized"
                readonly
              />
            </b-col>
            <!-- Net Prepayment Value -->

            <!-- Monthly Amortized Value -->
            <b-col md="6">
              <SpecialInputNumberField
                id="period-already-utilized"
                v-model="prepaymentAmortizationFormData.monthlyAmortizedValue"
                :label="$t('Monthly Amortized Value')"
                :placeholder="$t('Enter Monthly Amortized Value')"
                type="number"
                :error-name="$t('Monthly Amortized Value')"
                rules="required"
                for="period-already-utilized"
                readonly
              />
            </b-col>
            <!-- Monthly Amortized Value -->

            <!-- Payment Source GL -->
            <b-col md="6">
              <SelectField
                id="payment-source-general-ledger"
                v-model="prepaymentAmortizationFormData.paymentSourceGeneralLedger"
                :label="$t('Payment Source GL')"
                :placeholder="$t('Select')"
                :options="paymentSourceGeneralLedgerOptions"
                :reduce="option => option.value"
                :error-name="$t('Payment Source GL')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Payment Source GL -->

            <!-- Prepayment GL -->
            <b-col md="6">
              <SelectField
                id="prepayment-general-ledger"
                v-model="prepaymentAmortizationFormData.prepaymentGeneralLedger"
                :label="$t('Prepayment GL')"
                :placeholder="$t('Select')"
                :options="prepaymentGeneralLedgerOptions"
                :reduce="option => option.value"
                item="label"
                :error-name="$t('Prepayment GL')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Prepayment GL -->

            <!-- Amortization Expense GL -->
            <b-col md="6">
              <SelectField
                id="prepayment-general-ledger"
                v-model="prepaymentAmortizationFormData.amortizationExpenseGeneralLedger"
                :label="$t('Amortization Expense GL')"
                :placeholder="$t('Select')"
                :options="amortizationExpenseGLOptions"
                :reduce="option => option.value"
                :error-name="$t('Amortization Expense GL')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Amortization Expense GL -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchPrepaymentAmortization"
      @hidden="resetFilters"
    >
      <b-row class="match-height">
        <!-- Prepayment Name -->
        <b-col md="6">
          <InputField
            id="asset-name"
            v-model="filter.name"
            :label="$t('Prepayment Name')"
            :placeholder="$t('Enter Prepayment Name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Prepayment Name -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branch"
            :reduce="option => option.value"
            :label="$t('Branch')"
            :placeholder="$t('Select')"
            :options="branchOptions"
            item="label"
          />
        </b-col>
        <!-- Branch -->

        <!-- Utilization Start Date From -->
        <b-col md="6">
          <DatePickerField
            id="date-of-utilization-from"
            v-model="filter.startDate"
            :min-date="new Date()"
            :label="$t('Utilization Date From')"
            :config="datePickerConfig"
            placeholder="Utilization Date From"
          />
        </b-col>
        <!-- Utilization Start Date From -->

        <!-- Utilization Start Date To -->
        <b-col md="6">
          <DatePickerField
            id="date-of-utilization-to"
            v-model="filter.endDate"
            :min-date="new Date()"
            :label="$t('Utilization Date To')"
            :config="datePickerConfig"
            :placeholder="$t('Utilization Date To')"
          />
        </b-col>
        <!-- Utilization Start Date To -->
        <!-- Prepayment Category -->
        <b-col
          md="6"
        >
          <SelectField
            id="prepayment-category"
            v-model="filter.category"
            :label="$t('Prepayment Category')"
            :placeholder="$t('Select')"
            :options="categoryOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Prepayment Category -->
        <b-col md="6">
          <InputField
            id="prepayment-code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter Code')"
            type="text"
          />
        </b-col>
        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            item="label"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge, BForm, BDropdownItem, BDropdown, BFormRadioGroup,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'

import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { toastAlert } from '@core/mixins/toast'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapGetters, mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import moment from 'moment'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithYear, toDateWithYearFirst } from '@/helpers/formatDate'
import {
  toKobo, toBalance, formatAmount,
} from '@/helpers/formatAmount'
import DatePickerField from '../../forms/DatePickerField.vue'
import InputField from '../../forms/InputField.vue'
import InputNumberField from '../../forms/InputNumberField.vue'
import SpecialInputNumberField from '../../forms/SpecialInputNumberField.vue'
import SelectField from '../../forms/SelectField.vue'
import getStatusColor from '../../../helpers/getStatusColor'
import Button from '../../components/button/Button.vue'
import { accountPermissions } from '../../../helpers/permissions'
import { prepaymentCategory, statusList } from '../../../helpers/data'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BForm,
    BBadge,
    BSpinner,
    SelectField,
    TablePagination,
    TableEntries,
    ValidationObserver,
    InputField,
    InputNumberField,
    SpecialInputNumberField,
    BDropdownItem,
    BDropdown,
    FullScreenSpinner,
    Button,
    DatePickerField,
    BFormRadioGroup,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin, toastAlert],
  data() {
    return {
      formatAmount,
      toBalance,
      size: 300,
      isExisting: false,
      isNotExisting: true,
      isViewOnly: false,
      formatDate: toDateWithYear,
      toDateWithYearFirst,
      isLoading: false,
      isReadonly: false,
      isEditable: false,
      isDisabled: false,
      buttonLabel: this.$t('Save'),
      sortBy: 'name',
      required,
      modalTitle: this.$t('Create Prepayment Amortization'),
      sortDesc: false,
      showSpinner: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Prepayment Name' },
        { key: 'branchName', sortable: true, label: 'Branch' },
        { key: 'utilizationStartDate', sortable: true, label: 'Utilization Start Date' },
        { key: 'totalCoveragePeriod', sortable: true, label: 'Total Coverage Period (in months)' },
        { key: 'unutilizedCoveragePeriod', sortable: true, label: 'Un-utilized Coverage Period (in months)' },
        { key: 'expirationDate', sortable: true, label: 'Expiration Date' },
        { key: 'category', sortable: true, label: 'Prepayment Category' },
        { key: 'amount', sortable: true, label: 'Prepayment Amount' },
        { key: 'cummulativeAmortizedValue', sortable: true, label: 'Cumm. Amortized Value' },
        { key: 'netRepaymentValue', sortable: true, label: 'Net Prepayment Value' },
        { key: 'monthlyAmortizedValue', sortable: true, label: 'Monthly Amortized Value' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      prepaymentAmortizationList: [],
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
        ariaDateFormat: 'Y-m-d',
      },
      prepaymentAmortizationFormData: {
        name: '',
        description: '',
        branch: '',
        branchName: '',
        category: '',
        amount: '',
        existence: 'AlreadyExisting',
        utilizationStartDate: '',
        periodsAlreadyUtilized: '',
        totalCoveragePeriod: '',
        cummulativeAmortizedValue: '',
        unutilizedCoveragePeriod: '',
        netRepaymentValue: '',
        monthlyAmortizedValue: '',
        paymentSourceGeneralLedger: '',
        prepaymentGeneralLedger: '',
        amortizationExpenseGeneralLedger: '',
        id: '',
      },
      existenceOptions: [
        { value: 'AlreadyExisting', text: 'Already Existing' },
        { value: 'NotExisting', text: 'Not Existing' },
      ],
      // categoryOptions: prepaymentCategory,
      amortizationExpenseGLOptions: [],
      paymentSourceGeneralLedgerOptions: [],
      prepaymentGeneralLedgerOptions: [],
      permission: '',
      filter: {
        name: '',
        branch: '',
        startDate: '',
        endDate: '',
        category: '',
        status: '',
        code: '',
        sortColumn: '',
      },
      // statusOptions: statusList,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    categoryOptions() {
      return prepaymentCategory.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedStatusOptions() {
      return statusList.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translateExistenceOptions() {
      return this.existenceOptions.map(item => ({ ...item, text: item.text ? this.$t(item.text) : item.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchPrepaymentAmortization()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchPrepaymentAmortization()
    },
  },
  mounted() {
    this.getPermission()
    this.fetchPrepaymentAmortization()
    this.getPaymentSourceGL()
    this.getAmortizationExpenseGL()
    this.getPrepaymentGL()
  },

  methods: {
    ...mapActions({
      getPrepaymentAmortization: 'account/getprepaymentAmortizationList',
      updatePrepayment: 'account/updatePrepaymentAction',
      createPrepayment: 'account/createPrepaymentAction',
      updatePrepaymentStatus: 'account/updateStatus',
      getpaymentSourceGeneralLedger: 'account/getpaymentSourceGeneralLedger',
      getPrepaymentGeneralLedger: 'account/getPrepaymentGeneralLedger',
      getAmortiztionGeneralLedger: 'account/getAmortiztionGeneralLedger',
    }),
    getPermission() {
      if (this.actionType === 'save' || this.actionType === 'add') {
        this.permission = accountPermissions.CREATE_PREPAYMENT_AMORTIZATION
        return
      }
      if (this.actionType === 'edit') {
        this.permission = accountPermissions.MODIFY_PREPAYMENT_AMORTIZATION
        return
      }
      this.permission = accountPermissions.VIEW_PREPAYMENT_AMORTIZATION
    },

    setExistence(val) {
      if (val === 'AlreadyExisting') {
        this.isExisting = false
        this.isNotExisting = true
      } else {
        this.isExisting = true
        this.isNotExisting = false
        // this.prepaymentAmortizationFormData.accumulatedDepreciationValue = 0
      }
    },
    async onSelectBranchItem(items, activity, val) {
      if (val === 'view') {
        this.isViewOnly = true
      }
      this.isEditable = true
      this.prepaymentAmortizationFormData.name = items.name
      this.prepaymentAmortizationFormData.description = items.description
      this.prepaymentAmortizationFormData.branch = items.branch
      this.prepaymentAmortizationFormData.branchName = items.branchName
      this.prepaymentAmortizationFormData.category = items.category
      this.prepaymentAmortizationFormData.existence = items.existence
      this.prepaymentAmortizationFormData.amount = items.amount ? this.formatAmount(items.amount) : ''
      this.prepaymentAmortizationFormData.utilizationStartDate = items.utilizationStartDate
      this.prepaymentAmortizationFormData.periodsAlreadyUtilized = Number(items.periodsAlreadyUtilized)
      this.prepaymentAmortizationFormData.totalCoveragePeriod = Number(items.totalCoveragePeriod)
      this.prepaymentAmortizationFormData.cummulativeAmortizedValue = items.cummulativeAmortizedValue ? this.formatAmount(items.cummulativeAmortizedValue) : ''
      this.prepaymentAmortizationFormData.unutilizedCoveragePeriod = Number(items.unutilizedCoveragePeriod)
      this.prepaymentAmortizationFormData.netRepaymentValue = items.netRepaymentValue ? this.formatAmount(items.netRepaymentValue) : ''
      this.prepaymentAmortizationFormData.monthlyAmortizedValue = items.monthlyAmortizedValue ? this.formatAmount(items.monthlyAmortizedValue) : ''
      this.prepaymentAmortizationFormData.paymentSourceGeneralLedger = items.paymentSourceGeneralLedger
      this.prepaymentAmortizationFormData.prepaymentGeneralLedger = items.prepaymentGeneralLedger
      this.prepaymentAmortizationFormData.amortizationExpenseGeneralLedger = items.amortizationExpenseGeneralLedger
      this.prepaymentAmortizationFormData.id = items.id
      this.isEditable = true
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Prepayment Amortization') : this.$t('View Prepayment Amortization')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.prepaymentAmortizationRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const prepaymentText = this.$t('this Prepayment Amortization?')
          this.confirm(this.prepaymentAmortizationAction, { text: `${areYouSure} ${this.buttonLabel} ${prepaymentText}` })
        }
      })
    },
    async prepaymentAmortizationAction() {
      try {
        this.showSpinner = true
        this.isLoading = true
        const formData = {
          ...this.prepaymentAmortizationFormData,
          amount: toKobo(this.prepaymentAmortizationFormData.amount),
          periodsAlreadyUtilized: Number(this.prepaymentAmortizationFormData.periodsAlreadyUtilized),
          totalCoveragePeriod: Number(this.prepaymentAmortizationFormData.totalCoveragePeriod),
          cummulativeAmortizedValue: toKobo(this.prepaymentAmortizationFormData.cummulativeAmortizedValue),
          unutilizedCoveragePeriod: Number(this.prepaymentAmortizationFormData.unutilizedCoveragePeriod),
          monthlyAmortizedValue: toKobo(this.prepaymentAmortizationFormData.monthlyAmortizedValue),
          netRepaymentValue: toKobo(this.prepaymentAmortizationFormData.netRepaymentValue),
          initiatedBy: this.userDetails.email,

        }
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createPrepayment(formData)
          : await this.updatePrepayment(formData)
        const { status } = await response
        if (status === 200) {
          this.fetchPrepaymentAmortization()
          this.hideModal('modal-lg')
          const requestToText = this.$t('Your request to')
          const text = this.$t('Prepayment Amortization was successful')
          this.alertSuccess({ text: `${requestToText} ${this.buttonLabel} ${text}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('Prepayment Amortization failed. Reason')
          this.alertError({ text: `${requestToText} ${this.buttonLabel} ${reasonText}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
        this.showSpinner = false
      }
    },

    resetModal() {
      this.prepaymentAmortizationFormData.name = ''
      this.prepaymentAmortizationFormData.description = ''
      this.prepaymentAmortizationFormData.branch = ''
      this.prepaymentAmortizationFormData.branchName = ''
      this.prepaymentAmortizationFormData.category = ''
      this.prepaymentAmortizationFormData.existence = ''
      this.prepaymentAmortizationFormData.amount = ''
      this.prepaymentAmortizationFormData.utilizationStartDate = ''
      this.prepaymentAmortizationFormData.periodsAlreadyUtilized = ''
      this.prepaymentAmortizationFormData.totalCoveragePeriod = ''
      this.prepaymentAmortizationFormData.cummulativeAmortizedValue = ''
      this.prepaymentAmortizationFormData.unutilizedCoveragePeriod = ''
      this.prepaymentAmortizationFormData.netRepaymentValue = ''
      this.prepaymentAmortizationFormData.monthlyAmortizedValue = ''
      this.prepaymentAmortizationFormData.paymentSourceGeneralLedger = ''
      this.prepaymentAmortizationFormData.prepaymentGeneralLedger = ''
      this.prepaymentAmortizationFormData.amortizationExpenseGeneralLedger = ''
      this.isReadonly = false
      this.isLoading = false
      this.isEditable = false
      this.modalTitle = this.$t('Create Prepayment Amortization')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchPrepaymentAmortization()
    },
    async getPaymentSourceGL() {
      try {
        const response = await this.getpaymentSourceGeneralLedger({ permission: this.permission })
        const { status, data } = await response
        if (status === 200) {
          this.paymentSourceGeneralLedgerOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getPrepaymentGL() {
      try {
        const response = await this.getPrepaymentGeneralLedger({ permission: this.permission })
        const { status, data } = await response
        if (status === 200) {
          this.prepaymentGeneralLedgerOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getAmortizationExpenseGL() {
      try {
        const response = await this.getAmortiztionGeneralLedger({ permission: this.permission })
        const { status, data } = await response
        if (status === 200) {
          this.amortizationExpenseGLOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async fetchPrepaymentAmortization() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getPrepaymentAmortization({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.prepaymentAmortizationList = data.data.map(item => ({
            ...item,
            expirationDate: this.toDateWithYearFirst(moment(item.utilizationStartDate, 'YYYY-MM-DD').add(item.totalCoveragePeriod, 'months')),
          }))
          this.totalCount = data.recordCount
        } else {
          this.prepaymentAmortizationList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    computePrepayment() {
      const {
        amount,
        totalCoveragePeriod,
        periodsAlreadyUtilized,
        unutilizedCoveragePeriod,
        netRepaymentValue,
      } = this.prepaymentAmortizationFormData
      this.prepaymentAmortizationFormData.cummulativeAmortizedValue = Number((amount / totalCoveragePeriod) * periodsAlreadyUtilized)
      this.prepaymentAmortizationFormData.unutilizedCoveragePeriod = Number(totalCoveragePeriod - periodsAlreadyUtilized)
      this.prepaymentAmortizationFormData.netRepaymentValue = Number(amount - this.prepaymentAmortizationFormData.cummulativeAmortizedValue)
      this.prepaymentAmortizationFormData.monthlyAmortizedValue = Number(netRepaymentValue / unutilizedCoveragePeriod)
    },
    async prepaymentStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_PREPAYMENT_AMORTIZATION' : 'ACTIVATE_PREPAYMENT_AMORTIZATION'

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updatePrepaymentStatus({
            statusValues, actionType, routeName: 'PrepaymentAmortization', permission,
          })
          if (response.status === 200) {
            this.fetchPrepaymentAmortization()
            const requestToText = this.$t('Your request to')
            const text = this.$t('Prepayment Amortization was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
            this.showSpinner = false
          }
        } catch (error) {
          error.response.data.errors.forEach(responseError => {
            const requestToText = this.$t('Your request to')
            const reasonText = this.$t('Prepayment Amortization failed. Reason')
            this.alertError({ text: `${requestToText} ${action} ${reasonText}: ${responseError.errorMessage}` })
          })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const prepaymentText = this.$t('this Prepayment Amortization?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${prepaymentText}` })
    },

  },
}
</script>
